/**
 * Dragging a cleaning to another day on the Cleaning page (CAP-902).
 *
 * The crews' days are optimised constantly - cleanings are batched onto the
 * days a crew is already out - and the drawer's date picker is four steps for
 * what is one gesture. This is that gesture: pick a row up by its handle, drop
 * it on a day.
 *
 * **Only the DATE changes.** The time of day stays where it was, read in the
 * property's own zone, so a 10am cleaning dropped on Tuesday is Tuesday at
 * 10am whatever the browser's zone is.
 *
 * **Nothing here decides whether a move is allowed.** Whether a cleaning may
 * sit on a given day is admin-api's rule and it differs per source (a
 * reservation-sourced job can never be pulled before checkout), so a drop posts
 * and a refusal is surfaced. The one thing this file does gate is whether a row
 * offers a handle at all, and that mirrors the drawer's own "Move cleaning":
 * a scheduled job that has had its decision made.
 *
 * The pure helpers are separate from the components so the date arithmetic and
 * the keyboard navigation can be exercised without rendering a drag.
 */

import type { ReactNode } from 'react'
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MeasuringStrategy,
  PointerSensor,
  useDndContext,
  useDroppable,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
  type Modifier,
} from '@dnd-kit/core'
import { GripVertical } from 'lucide-react'
import type { CleaningJob } from '@/api/client'
import { formatCleaningDay } from '@/components/cleaning/presentation'
import {
  collisionDetection,
  dayDropId,
  dayKeyboardCoordinates,
  dayOfDropId,
  instantOnDay,
  useDragRow,
  type DragData,
} from '@/components/cleaning/day-move'

/**
 * The carried row only moves up and down. It is as wide as the table, so
 * letting it follow the pointer sideways slid it off the page, and a day is
 * chosen by height alone anyway.
 */
const lockToColumn: Modifier = ({ transform }) => ({ ...transform, x: 0 })

interface DayDragProviderProps {
  today: string | null
  /** A job's zone, for reading "10am" where the house is. */
  timeZoneFor: (propertyId: string) => string
  /** Called with the instant to move the job to. */
  onMove: (move: {
    job: CleaningJob
    propertyName: string
    fromDay: string
    toDay: string
    scheduledAt: string
  }) => void
  /** The row as it looks while carried, built by the table from the row's own cells. */
  renderPreview: (job: CleaningJob) => ReactNode
  children: ReactNode
}

/**
 * The drag context for the day-grouped table: sensors, the drop, the preview
 * that follows the pointer, and what a screen reader is told.
 */
export function DayDragProvider({
  today,
  timeZoneFor,
  onMove,
  renderPreview,
  children,
}: DayDragProviderProps) {
  const sensors = useSensors(
    // A few pixels of travel before a drag starts, so pressing the handle to
    // read its tooltip is not a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: dayKeyboardCoordinates })
  )

  const dayLabel = (day: string | null) => formatCleaningDay(day, today)

  const announcements: Announcements = {
    onDragStart: ({ active }) => {
      const data = active.data.current as DragData | undefined
      return `Picked up the cleaning at ${data?.propertyName ?? 'this property'}, on ${dayLabel(data?.day ?? null)}.`
    },
    onDragOver: ({ over }) => {
      const day = over ? dayOfDropId(over.id) : null
      return day ? `Over ${dayLabel(day)}.` : 'Not over a day.'
    },
    onDragEnd: ({ active, over }) => {
      const data = active.data.current as DragData | undefined
      const day = over ? dayOfDropId(over.id) : null
      return day && day !== data?.day
        ? `Moving the cleaning at ${data?.propertyName ?? 'this property'} to ${dayLabel(day)}.`
        : 'Dropped where it was. Nothing moved.'
    },
    onDragCancel: () => 'Cancelled. Nothing moved.',
  }

  function handleDragEnd({ active, over }: DragEndEvent) {
    const data = active.data.current as DragData | undefined
    const toDay = over ? dayOfDropId(over.id) : null
    if (!data || !toDay || !data.day || toDay === data.day) return
    const scheduledAt = instantOnDay(data.job, toDay, timeZoneFor(data.job.property_id))
    if (!scheduledAt) return
    onMove({
      job: data.job,
      propertyName: data.propertyName,
      fromDay: data.day,
      toDay,
      scheduledAt,
    })
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      modifiers={[lockToColumn]}
      // Always, not just when a drag starts: the day under the pointer opens a
      // slot and the day it left closes one, which moves every group below
      // while the pointer is held still. Rectangles measured at pick-up would
      // then point at where the days WERE.
      measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
      onDragEnd={handleDragEnd}
      accessibility={{
        announcements,
        screenReaderInstructions: {
          draggable:
            'Press space to pick up this cleaning. Use the up and down arrow keys to choose a day, space to drop it there, or escape to cancel. Only the date changes; the time stays.',
        },
      }}
    >
      {children}
      <DragPreview renderPreview={renderPreview} />
    </DndContext>
  )
}

/**
 * What follows the pointer: the row itself, lifted. The real row stays put and
 * dims, so a cancelled drag has nothing to put back, and where it would land
 * is shown by the slot its day opens, not by anything on the preview.
 */
function DragPreview({ renderPreview }: { renderPreview: (job: CleaningJob) => ReactNode }) {
  const { active } = useDndContext()
  const data = active?.data.current as DragData | undefined

  return (
    <DragOverlay dropAnimation={null}>
      {data ? (
        <div
          data-drag-preview
          className="cursor-grabbing overflow-hidden rounded-card border border-mintBorder shadow-2xl ring-1 ring-mint/30"
        >
          {renderPreview(data.job)}
        </div>
      ) : null}
    </DragOverlay>
  )
}

interface DayGroupProps {
  day: string | null
  /** The day's header row, which the slot opens right under. */
  header: ReactNode
  children: ReactNode
}

/**
 * One day of the table: its header and every cleaning under it, as a single
 * drop target. A `tbody` per day, so a drop anywhere on the day counts, not
 * just on its header.
 *
 * While a cleaning is over it, the day opens an empty slot the height of the
 * row being carried, which is what it would be like for the row to be there.
 * The slot is the day's first row, under its header: a cleaning is dragged
 * into a day to go on top, and where it settles once saved is up to the sort.
 * Never in the day it came from: dropping back is not a move.
 */
export function DayGroup({ day, header, children }: DayGroupProps) {
  const { active } = useDndContext()
  const { setNodeRef, isOver } = useDroppable({
    id: dayDropId(day ?? ''),
    // A job with no day has nowhere to be dropped on.
    disabled: !day,
  })
  const fromDay = (active?.data.current as DragData | undefined)?.day
  const receiving = isOver && Boolean(day) && day !== fromDay
  // As tall as the row that is being carried, less the cell padding the slot
  // adds back, so the day opens by exactly one row.
  const rowHeight = active?.rect.current.initial?.height ?? 48

  return (
    <tbody
      ref={setNodeRef}
      data-day={day ?? undefined}
      data-drop-target={receiving ? 'true' : undefined}
    >
      {header}
      {receiving && (
        <tr data-drop-slot aria-hidden="true">
          <td colSpan={7} className="px-2 py-1.5">
            <div
              className="rounded-card border-2 border-dashed border-mintBorder bg-mintHover"
              style={{ height: Math.max(rowHeight - 12, 24) }}
            />
          </td>
        </tr>
      )}
      {children}
    </tbody>
  )
}

interface DraggableRowProps {
  job: CleaningJob
  propertyName: string
  day: string | null
  /** An action on the job is in flight: no handle until it lands. */
  pending: boolean
  className: string
  onClick: () => void
  /** The crew-colour cell, which stays the row's first. */
  leading: ReactNode
  children: ReactNode
}

/**
 * A table row that can be picked up by its handle. The row is what moves, so
 * its own rectangle is what a keyboard drag steps from, but only the grip
 * starts one: every other control on the row stays an ordinary click.
 */
export function DraggableRow({
  job,
  propertyName,
  day,
  pending,
  className,
  onClick,
  leading,
  children,
}: DraggableRowProps) {
  const { setNodeRef, setActivatorNodeRef, listeners, attributes, isDragging, draggable } =
    useDragRow(job, propertyName, day)

  return (
    <tr
      ref={setNodeRef}
      onClick={onClick}
      className={`${className} ${isDragging ? 'opacity-40' : ''}`}
    >
      {leading}
      <td className="border-b border-border px-0 py-2 text-center align-middle">
        {draggable && !pending && (
          <button
            type="button"
            ref={setActivatorNodeRef}
            {...attributes}
            {...listeners}
            // The row opens the drawer on click; pressing the grip must not.
            onClick={event => event.stopPropagation()}
            aria-label={`Move the cleaning at ${propertyName} to another day`}
            title="Drag to another day"
            className="inline-flex h-7 w-6 cursor-grab touch-none items-center justify-center rounded text-textFaint hover:text-text focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint active:cursor-grabbing"
          >
            <GripVertical className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </td>
      {children}
    </tr>
  )
}
